import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock, MessagesSquare, MessageSquareText, MoreVertical, Trophy, Users, Loader2, Building2, User as UserIcon } from 'lucide-react';
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Group } from '@/types/support';
import { GroupCategoryBadges } from './GroupCategoryBadges';
import { manualSyncOrganizationGroups } from '@/lib/firebase/queries/manual-sync-trigger';
import { GroupLeaderboard } from './GroupLeaderboard';
import { GroupPostsDialog } from './GroupPostsDialog';

import { CreatePost } from './CreatePost';
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isGroupMember, getGroupMembers, leaveGroup } from '@/lib/firebase/queries/group-members';
import { getMemberRowsOfGroups, getUserGroupMemberRows, getUserOrganizationIds } from '@/lib/firebase/queries/groupListData';
import { countListMembers, countSourceIds, resolveListMembership } from '@/lib/groups/listMembership';
import { deleteGroup } from '@/lib/firebase/queries/groups';
import { subscribeToUnreadCounts } from '@/lib/firebase/queries/groupReads';
import { formatUnreadBadge } from '@/lib/groups/groupUnread';
import {
  effectiveFilterKey,
  filterGroupsByKind,
  groupBadge,
  groupFilterOptions,
  groupPhotoUri,
  type GroupFilterKey,
} from '@/lib/groups/groupKind';
import { useOrgNames } from '@/hooks/useOrgNames';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GroupMembers } from './GroupMembers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GroupListProps {
  groups: Group[];
  onOpenChat: (group: Group) => void;
  onOpenPosts: (group: Group) => void;
  onEditGroup: (group: Group) => void;
}

export function GroupList({ groups, onOpenChat, onOpenPosts, onEditGroup }: GroupListProps) {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [selectedGroupForLeaderboard, setSelectedGroupForLeaderboard] = useState<Group | null>(null);
  const [selectedGroupForPosts, setSelectedGroupForPosts] = useState<Group | null>(null);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<Group | null>(null);
  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});

  const [showCreatePost, setShowCreatePost] = useState(false);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [editorGroups, setEditorGroups] = useState<Set<string>>(new Set());
  const [organizationMemberGroups, setOrganizationMemberGroups] = useState<Set<string>>(new Set());
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  const groupIdsKey = useMemo(() => filteredGroups.map(g => g.id).sort().join(','), [filteredGroups]);

  // Org vs personal (mobile ListBadges design): the badge on the name line and
  // the «الكل / <org> / شخصية» pills, shown only when both kinds are listed.
  const [filterKey, setFilterKey] = useState<GroupFilterKey>('all');
  const badgeOrgIds = useMemo(
    () => filteredGroups.map((g) => g.organization).filter((id): id is string => !!id),
    [filteredGroups],
  );
  const orgNames = useOrgNames(badgeOrgIds, language === 'ar');
  const filterOptions = useMemo(() => groupFilterOptions(filteredGroups), [filteredGroups]);
  const activeFilter = effectiveFilterKey(filterKey, filterOptions);
  const visibleGroups = useMemo(() => filterGroupsByKind(filteredGroups, activeFilter), [filteredGroups, activeFilter]);

  // Per-group unread chat messages from the server read state (namat#36 P2).
  useEffect(() => {
    if (!user?.uid || !groupIdsKey) {
      setUnreadCounts({});
      return;
    }
    return subscribeToUnreadCounts(groupIdsKey.split(','), user.uid, setUnreadCounts);
  }, [user?.uid, groupIdsKey]);

  const renderUnreadBadge = (groupId: string) => {
    const label = formatUnreadBadge(unreadCounts[groupId] ?? 0);
    if (!label) return null;
    return (
      <span
        className="ms-1 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white text-[#3d8a1f] text-[0.7rem] font-bold leading-5 text-center"
        aria-label={`${label} ${t('support.unreadMessages')}`}
        data-testid={`group-unread-${groupId}`}
      >
        {label}
      </span>
    );
  };

  // Member counts after the list is shown: the listed groups' groupMembers
  // rows, 30 groups per query, no users reads (they used to be read one by
  // one per member and the list waited for them). Organization managers are
  // members like anyone else and are counted, as on mobile.
  const loadMemberCounts = async (listed: Group[], allGroups: Group[], isCurrent: () => boolean) => {
    try {
      const rows = await getMemberRowsOfGroups(countSourceIds(listed, allGroups));
      if (isCurrent()) setMemberCounts(countListMembers(listed, allGroups, rows));
    } catch (error) {
      console.error('❌ Error loading member counts:', error);
    }
  };

  useEffect(() => {
    if (!user) return;
    let current = true;
    const isCurrent = () => current;
    // Spinner only for the first load: later snapshots (latest posts filled
    // in, a group renamed) update the list in place without flashing it.
    const filterGroups = async () => {
      try {
        // Two reads of the user's own rows instead of three queries per group.
        const [memberRows, userOrgIds] = await Promise.all([
          getUserGroupMemberRows(user.uid),
          getUserOrganizationIds(user.uid),
        ]);
        if (!current) return;
        const membership = resolveListMembership(groups, user.uid, memberRows, userOrgIds);
        setEditorGroups(membership.editorIds);
        setOrganizationMemberGroups(membership.orgMemberGroupIds);

        // 🎯 Hide sections that have only one child group and show the child group directly
        const filteredGroupsList = optimizeGroupDisplay(groups.filter((g) => membership.memberIds.has(g.id)));
        setFilteredGroups(filteredGroupsList);
        setLoading(false);

        if (filteredGroupsList.length > 0) void loadMemberCounts(filteredGroupsList, groups, isCurrent);
      } catch (error) {
        console.error('❌ Error resolving group membership:', error);
        if (current) setLoading(false);
      }
    };

    filterGroups();
    return () => { current = false; };
  }, [groups, user]);

  // Function to hide sections with only one child and show the child directly
  const optimizeGroupDisplay = (groupsList: Group[]): Group[] => {
    // Build parent-child map
    const parentChildMap = new Map<string, Group[]>();
    const childParentMap = new Map<string, string>();
    
    // First pass: map children to parents
    groupsList.forEach(group => {
      if (group.parentGroupId) {
        if (!parentChildMap.has(group.parentGroupId)) {
          parentChildMap.set(group.parentGroupId, []);
        }
        parentChildMap.get(group.parentGroupId)!.push(group);
        childParentMap.set(group.id, group.parentGroupId);
      }
    });
    
    // Find sections with exactly one child
    const sectionsToHide = new Set<string>();
    const childrenToShow = new Set<string>();
    
    groupsList.forEach(group => {
      if (group.groupType === 'parent') {
        const children = parentChildMap.get(group.id) || [];
        if (children.length === 1) {
          sectionsToHide.add(group.id);
          childrenToShow.add(children[0].id);
          console.log(`🎯 Hiding section "${group.name}" and showing child "${children[0].name}" directly`);
        }
      }
    });
    
    // Filter out sections to hide
    const optimizedGroups = groupsList.filter(group => {
      // Hide sections that have only one child
      if (sectionsToHide.has(group.id)) {
        return false;
      }
      
      // Keep all other groups (including the single children of hidden sections)
      return true;
    });
    
    console.log(`🎯 Group display optimization: ${groupsList.length} → ${optimizedGroups.length} groups`);
    return optimizedGroups;
  };

  const handleDelete = async () => {
    if (!groupToDelete || !user) return;

    try {
      setIsDeleting(true);
      const success = await deleteGroup(groupToDelete.id, user.uid);
      if (success) {
        toast.success(t('support.groupDeleted'));
        setGroupToDelete(null);
      } else {
        toast.error(t('support.groupDeleteError'));
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(t('support.groupDeleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const [groupToLeave, setGroupToLeave] = useState<Group | null>(null);

  const handleLeave = async () => {
    if (!groupToLeave || !user) return;

    try {
      setIsLeaving(true);
      const success = await leaveGroup(groupToLeave.id, user.uid);
      if (success) {
        toast.success(t('support.groupLeft'));
        setGroupToLeave(null);
        // Refresh the groups list by refiltering
        const updatedGroups = groups.filter(g => g.id !== groupToLeave.id);
        setFilteredGroups(updatedGroups);
      } else {
        toast.error(t('support.groupLeaveError'));
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error(t('support.groupLeaveError'));
    } finally {
      setIsLeaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderKindBadge = (group: Group) => {
    const badge = groupBadge(group, orgNames);
    const isOrg = badge.kind === 'org';
    const label = isOrg ? badge.orgName : t('support.kindPersonal');
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold max-w-[150px] shrink-0 ${
          isOrg ? 'bg-[#E6F0FB] text-[#1E4E8C]' : 'bg-[#F1F7EE] text-[#2F6F1F]'
        }`}
        data-testid={`group-kind-badge-${group.id}`}
      >
        {isOrg ? <Building2 className="h-3 w-3 shrink-0" /> : <UserIcon className="h-3 w-3 shrink-0" />}
        {label && <span className="truncate">{label}</span>}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {filterOptions.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" role="group" aria-label={t('support.groupFilters')}>
          {filterOptions.map((option) => {
            const selected = option.key === activeFilter;
            const label = option.key === 'all'
              ? t('support.filterAll')
              : option.key === 'personal'
                ? t('support.kindPersonal')
                : orgNames[option.orgId as string] || '…';
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setFilterKey(option.key)}
                aria-pressed={selected}
                data-testid={`groups-filter-${option.key}`}
                className={`min-h-9 px-3.5 rounded-full border text-sm whitespace-nowrap transition-colors ${
                  selected
                    ? 'bg-[#004250] border-[#004250] text-white'
                    : 'bg-white border-[#D5DDE0] text-[#004250] hover:bg-muted/50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
      {visibleGroups.map((group, index) => (
        <motion.div
          key={group.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="rounded-lg border bg-card hover:bg-muted/50 transition-colors"
        >
          <div className="p-4">
            <div className="flex flex-col gap-4">
              <div className="flex-1 min-w-0">
                {/* Group Header - Name and Categories */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {groupPhotoUri(group) && (
                        <img
                          src={groupPhotoUri(group) as string}
                          alt=""
                          className="h-10 w-10 rounded-full object-cover shrink-0"
                          data-testid={`group-photo-${group.id}`}
                        />
                      )}
                      {group.categories.length > 1 ? (
                        <span className="text-lg" title={t('support.multiCategoryGroup')}>👥</span>
                      ) : (
                        <GroupCategoryBadges categories={group.categories} />
                      )}
                      <h3 className="font-medium text-lg">{group.name}</h3>
                      {renderKindBadge(group)}
                      {group.isPrivate && (
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      )}
                      {/* Mobile Member Count - Next to group name */}
                      <Badge 
                        variant="secondary" 
                        className="text-xs md:hidden"
                      >
                        {memberCounts[group.id] ?? '...'} {t('support.members')}
                      </Badge>
                    </div>
                    {!group.organization && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(editorGroups.has(group.id) || group.creatorId === user?.uid || (group.organization && organizationMemberGroups.has(group.id))) && (
                            <>
                              <DropdownMenuItem onClick={() => onEditGroup(group)}>
                                {t('support.editGroup')}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setSelectedGroupForMembers(group)}>
                                {t('support.manageMembers')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setGroupToDelete(group)}
                              >
                                {t('support.deleteGroup')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {!group.organization && !(editorGroups.has(group.id) || group.creatorId === user?.uid) && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setGroupToLeave(group)}
                              disabled={isLeaving}
                            >
                              {isLeaving ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  {t('common.leaving')}
                                </>
                              ) : (
                                t('support.leaveGroup')
                              )}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground mt-2">
                  {group.description}
                </p>

                {/* Meta Info */}
                <div className="mt-4 space-y-3">
                  {/* Desktop Layout: Member count and buttons in same row */}
                  <div className="hidden md:flex md:items-center md:justify-between">
                    <Badge 
                      variant="secondary" 
                      className="text-xs"
                    >
                      {memberCounts[group.id] ?? '...'} {t('support.members')}
                    </Badge>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        onClick={() => onOpenChat(group)}
                        className="bg-[#7dda55] hover:bg-[#6dc447] text-white px-2"
                      >
                        <MessagesSquare className="h-4 w-4 mx-1" />
                        {t('support.liveChat')}
                        {renderUnreadBadge(group.id)}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedGroupForPosts(group)}
                        className="px-2"
                      >
                        <MessageSquareText className="h-4 w-4 mx-1" />
                        {t('support.allPosts')}
                      </Button>
                      {/* Only show leaderboard for personal groups (not organization groups) */}
                      {!group.organization && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedGroupForLeaderboard(group)}
                          className="px-2"
                        >
                          <Trophy className="h-4 w-4 mx-1" />
                          {t('support.leaderboard')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Mobile Layout: Full width buttons */}
                  <div className="md:hidden">
                    <div className="flex gap-2 w-full">
                      <Button 
                        size="sm"
                        onClick={() => onOpenChat(group)}
                        className="bg-[#7dda55] hover:bg-[#6dc447] text-white flex-1 px-1"
                      >
                        <MessagesSquare className="h-4 w-4 mx-1 shrink-0" />
                        <span className="truncate text-xs">{t('support.liveChat')}</span>
                        {renderUnreadBadge(group.id)}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedGroupForPosts(group)}
                        className="flex-1 px-1"
                      >
                        <MessageSquareText className="h-4 w-4 mx-1 shrink-0" />
                        <span className="truncate text-xs">{t('support.allPosts')}</span>
                      </Button>
                      {/* Only show leaderboard for personal groups (not organization groups) */}
                      {!group.organization && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedGroupForLeaderboard(group)}
                          className="flex-1 px-1"
                        >
                          <Trophy className="h-4 w-4 mx-1 shrink-0" />
                          <span className="truncate text-xs">{t('support.leaderboard')}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Last Post */}
                {group.lastPost && (
                  <div className="flex items-center gap-2 mt-4 p-2 rounded-md bg-muted/50">
                    <MessageSquareText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium truncate">
                          {group.lastPost.userName}
                        </span>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {format(group.lastPost.createdAt, 'PPp')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {group.lastPost.content}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      ))}

      {filteredGroups.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {t('support.noGroups')}
        </div>
      )}

      {selectedGroupForLeaderboard && (
        <GroupLeaderboard
          groupId={selectedGroupForLeaderboard.id}
          open={!!selectedGroupForLeaderboard}
          onOpenChange={(open) => !open && setSelectedGroupForLeaderboard(null)}
          organizationId={selectedGroupForLeaderboard.organization}
        />
      )}

      {selectedGroupForPosts && (
        <GroupPostsDialog
          group={selectedGroupForPosts}
          open={!!selectedGroupForPosts}
          onOpenChange={(open) => !open && setSelectedGroupForPosts(null)}
        />
      )}

      {showCreatePost && selectedGroupForPosts && (
        <CreatePost
          group={selectedGroupForPosts}
          open={showCreatePost}
          onOpenChange={setShowCreatePost}
        />
      )}

      {selectedGroupForMembers && (
        <Dialog 
          open={!!selectedGroupForMembers} 
          onOpenChange={(open) => !open && setSelectedGroupForMembers(null)}
        >
          <DialogContent 
            className={`max-w-2xl max-h-[80vh] flex flex-col ${language === 'ar' ? 'rtl' : 'ltr'}`}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          >
            <DialogHeader className={language === 'ar' ? 'text-right' : 'text-left'}>
              <DialogTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                {t('support.manageMembers')} - {selectedGroupForMembers.name}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Manage group members for {selectedGroupForMembers.name}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="flex-1 pr-4">
              <GroupMembers group={selectedGroupForMembers} />
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}



      {showLeaderboard && (
        <GroupLeaderboard
          groupId={selectedGroupForLeaderboard?.id || ''}
          open={showLeaderboard}
          onOpenChange={setShowLeaderboard}
        />
      )}

      <AlertDialog open={!!groupToLeave} onOpenChange={(open) => !open && setGroupToLeave(null)}>
        <AlertDialogContent className={language === 'ar' ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={language === 'ar' ? 'text-right' : 'text-left'}>
              {t('support.leaveGroupTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className={language === 'ar' ? 'text-right' : 'text-left'}>
              {groupToLeave && t('support.leaveGroupMessage', { group: groupToLeave.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={language === 'ar' ? 'flex-row-reverse gap-2' : 'flex-row gap-2'}>
            <AlertDialogCancel disabled={isLeaving}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeave}
              disabled={isLeaving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLeaving ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                  {t('common.leaving')}
                </>
              ) : (
                t('support.leaveGroup')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!groupToDelete} onOpenChange={(open) => !open && setGroupToDelete(null)}>
        <AlertDialogContent className={language === 'ar' ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={language === 'ar' ? 'text-right' : 'text-left'}>
              {t('support.deleteGroupTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className={language === 'ar' ? 'text-right' : 'text-left'}>
              {t('support.deleteGroupMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={language === 'ar' ? 'flex-row-reverse gap-2' : 'flex-row gap-2'}>
            <AlertDialogCancel disabled={isDeleting}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                  {t('common.deleting')}
                </>
              ) : (
                t('support.deleteGroup')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}